import { axiosConfig, patch, type RequestConfig } from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type {
  ElderAdmission,
  ElderAdmissionPayload,
  ElderAdmissionRecord,
  ElderAdmissionStats,
} from "@project/types/elder-admission";
import { JIVANDHARA_BASE, fetchListPage, unwrapItem } from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/elder-admissions`;
const SILENT: RequestConfig = { showNotification: false };

class ElderAdmissionService {
  listAdmissionsPage = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<ElderAdmissionRecord, ElderAdmissionStats>> => {
    return fetchListPage<ElderAdmissionRecord, ElderAdmissionStats>(
      `${BASE}/`,
      query
    );
  };

  getAdmission = async (
    id: number
  ): Promise<ElderAdmissionRecord | undefined> => {
    const response = await axiosConfig.get<
      ApiItemResponse<ElderAdmissionRecord> | ElderAdmissionRecord
    >(`${BASE}/${id}/`, SILENT);
    return unwrapItem(response.data);
  };

  create = async (payload: ElderAdmissionPayload): Promise<ElderAdmission> => {
    const response = await axiosConfig.post<
      ApiItemResponse<ElderAdmission> | ElderAdmission
    >(`${BASE}/`, payload, SILENT);
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to submit application.");
    }
    return item;
  };

  update = async (
    id: number,
    payload: ElderAdmissionPayload
  ): Promise<ElderAdmissionRecord | undefined> => {
    const data = await patch<
      ApiItemResponse<ElderAdmissionRecord> | ElderAdmissionRecord,
      ElderAdmissionPayload
    >(`${BASE}/${id}/`, payload, SILENT);
    return unwrapItem(data);
  };

  approve = async (id: number): Promise<ElderAdmissionRecord> => {
    const response = await axiosConfig.post<
      ApiItemResponse<ElderAdmissionRecord> | ElderAdmissionRecord
    >(`${BASE}/${id}/approve/`, {}, SILENT);
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to approve application.");
    }
    return item;
  };

  reject = async (id: number): Promise<ElderAdmissionRecord> => {
    const response = await axiosConfig.post<
      ApiItemResponse<ElderAdmissionRecord> | ElderAdmissionRecord
    >(`${BASE}/${id}/reject/`, {}, SILENT);
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to reject application.");
    }
    return item;
  };
}

export const elderAdmissionService = new ElderAdmissionService();
